import { FC, ReactNode, useId, useCallback } from 'react';
import { css, Theme } from '@emotion/react';
import { Root as RadixCheckbox, CheckedState } from '@radix-ui/react-checkbox';
import { Typography } from 'client/components/Typography';

const squareEmptyIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `fill='none' viewBox='0 0 256 256'%3E%3Crect width='256' height='256' fill='none'%3E%3C/` +
  `rect%3E%3Crect x='40' y='40' width='176' height='176' rx='8' fill='none' stroke='${theme.color.grey[80].replace('#', '%23')}' ` +
  `stroke-linecap='round' stroke-linejoin='round' stroke-width='24'%3E%3C/rect%3E%3C/svg%3E");`;

const squareCheckIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `viewBox='0 0 256 256'%3E%3Cpath fill='none' d='M0 0h256v256H0z'/%3E%3Cpath fill='${theme.color.primary[100].replace('#', '%23')}' ` +
  `d='M208 32H48a16 16 0 0 0-16 16v160a16 16 0 0 0 16 16h160a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16Zm-30.5 77.8-58.6 56a8.1 ` +
  `8.1 0 0 1-5.6 2.2 7.9 7.9 0 0 1-5.5-2.2l-29.3-28a8 8 0 1 1 11-11.6l23.8 22.7 53.2-50.7a8 8 0 0 1 11 11.6Z'/%3E%3C/svg%3E");`;

const checkboxStyle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  align-self: flex-start;

  > * {
    cursor: pointer;
  }

  > button {
    position: relative;
    width: 26px;
    height: 26px;
    background: ${squareEmptyIconURI(theme)};
    background-size: 100%;
    border: none;

    transform: translateX(-3px);

    &:focus-visible {
      outline: solid 4px ${theme.color.primary[100]};
      outline-offset: 4px;
    }

    &[data-state='checked'] {
      background-image: ${squareCheckIconURI(theme)};
    }
  }

  > label {
    padding-left: 4px;
    font-weight: bold;
    line-height: 28px;
    user-select: none;
    color: ${theme.color.foreground};
  }
`;

export interface CheckboxProps {
  value?: boolean;
  children: ReactNode;
  onChange: (checked: boolean) => void;
}

export const Checkbox: FC<CheckboxProps> = ({ value, children, onChange, ...other }) => {
  const id = useId();

  const handleChange = useCallback((checked: CheckedState) => onChange(!!checked), [onChange]);

  return (
    <div css={checkboxStyle} {...other}>
      <RadixCheckbox id={id} checked={value} onCheckedChange={handleChange} />
      <Typography {...({ htmlFor: id } as any)} variant="body1" component="label">
        {children}
      </Typography>
    </div>
  );
};
